import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api';
import { getPublicCorrection } from '../../lib/manualCorrectionsApi';
import { ManualCorrectionCard } from './ManualCorrectionCard';

export function ManualCorrectionReceiptPage({
  publicId,
  onNavigate,
}: {
  publicId: string;
  onNavigate: (path: string) => void;
}): JSX.Element {
  const query = useQuery({
    queryKey: ['manual-correction-status', publicId],
    queryFn: () => getPublicCorrection(publicId),
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 404) && failureCount < 2,
  });

  return (
    <main className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8 sm:px-6">
      <button
        type="button"
        onClick={() => onNavigate('/manual-corrections')}
        className="rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold hover:border-primary"
      >
        ← К форме и реестру
      </button>

      <header>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Receipt / статус</p>
        <h1 className="mt-2 text-3xl font-semibold">Статус заявки</h1>
        <p className="mt-2 text-muted-foreground">
          Сохраните UUID или адрес страницы, чтобы вернуться к результату проверки позже.
        </p>
      </header>

      {query.isLoading ? (
        <div
          role="status"
          aria-label="Загружаем статус заявки"
          aria-busy="true"
          className="h-48 animate-pulse rounded-lg border border-border bg-muted"
        />
      ) : query.error instanceof ApiError && query.error.status === 404 ? (
        <section role="status" className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-2xl font-semibold">Заявка не найдена</h2>
          <p className="mt-2 text-muted-foreground">
            Проверьте UUID. Отклонённая заявка также могла быть удалена после завершения срока хранения.
          </p>
        </section>
      ) : query.isError ? (
        <section role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-6">
          <h2 className="text-2xl font-semibold">Не удалось получить статус</h2>
          <p className="mt-2">{query.error instanceof ApiError ? query.error.message : 'Повторите запрос позже.'}</p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="mt-4 rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold hover:border-primary"
          >
            Повторить
          </button>
        </section>
      ) : query.data ? (
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Заявка принята</h2>
          <ManualCorrectionCard correction={query.data} />
        </section>
      ) : null}
    </main>
  );
}
